import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Space, Typography, Alert, Progress, Tag } from "antd";
import { 
  MdOutlineDelete, 
  MdOutlineWarning, 
  MdOutlineCheckCircle,
  MdOutlineClose 
} from "react-icons/md";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const DeleteConfirmationModal = ({
  visible,
  onCancel,
  onConfirm,
  staffName,
  staffPosition,
  staffId,
  loading = false,
}) => {
  const [inputDelete, setInputDelete] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (!visible) {
      setInputDelete("");
      setIsConfirmed(false);
    }
  }, [visible]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputDelete(value);
    setIsConfirmed(value === "DELETE");
    
    // Shake animation for wrong input
    if (value && value !== "DELETE" && value.length > 3) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isConfirmed) {
      handleConfirm();
    }
  };

  return (
    <Modal
      title={
        <div style={styles.modalTitle}>
          <div style={styles.iconWrapper}>
            <MdOutlineDelete style={styles.deleteIcon} />
          </div>
          <span style={styles.titleText}>លុបបុគ្គលិក</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={520}
      centered
      closable={!loading}
      maskClosable={!loading}
      footer={null}
      className="delete-modal"
      style={styles.modal}
    >
      <div style={styles.content}>
        {/* Warning Alert */}
        <Alert
          message={<span style={{ color: "#92400E", fontWeight: 600 }}>ការព្រមាន</span>}
          description={<span style={{ color: "#92400E" }}>ការលុបនេះមិនអាចត្រឡប់វិញបានទេ។ ទិន្នន័យទាំងអស់របស់បុគ្គលិកនឹងត្រូវលុបចោលជាអចិន្ត្រៃយ៍។</span>}
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined style={{ color: "#92400E" }} />}
          style={styles.alert}
        />

        {/* Staff Info Card */}
        <div style={styles.staffCard}>
          <div style={styles.staffAvatar}>
            <MdOutlineDelete style={styles.avatarIcon} />
          </div>
          <div style={styles.staffInfo}>
            <Text style={styles.staffLabel}>បុគ្គលិកដែលត្រូវលុប</Text>
            <Title level={4} style={styles.staffName}>
              {staffName || "N/A"}
            </Title>
            <Text style={styles.staffPosition}>
              {staffPosition || "N/A"}
            </Text>
            {staffId && (
              <Tag color="default" style={styles.staffId}>
                ID: {staffId}
              </Tag>
            )}
          </div>
        </div>

        {/* Confirmation Section */}
        <div style={styles.confirmationSection}>
          <div style={styles.confirmationHeader}>
            <MdOutlineWarning style={styles.warningIcon} />
            <Text strong style={styles.confirmationText}>
              ដើម្បីបញ្ជាក់ការលុប សូមបញ្ចូលពាក្យ "DELETE"
            </Text>
          </div>

          <Input
            value={inputDelete}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type DELETE to confirm"
            disabled={loading}
            autoFocus
            style={{
              ...styles.confirmInput,
              ...(inputDelete === "DELETE" ? styles.inputSuccess : {}),
              ...(shake ? styles.inputShake : {}),
            }}
            prefix={
              inputDelete === "DELETE" ? (
                <MdOutlineCheckCircle style={{ color: "#52c41a", fontSize: 18 }} />
              ) : (
                <MdOutlineWarning style={{ color: "#faad14", fontSize: 18 }} />
              )
            }
          />

          {/* Validation Feedback */}
          {inputDelete && inputDelete !== "DELETE" && (
            <div style={styles.errorFeedback}>
              <MdOutlineClose style={{ color: "#ff4d4f", fontSize: 14 }} />
              <Text style={styles.errorText}>
                សូមបញ្ចូលពាក្យ "DELETE" ឲ្យបានត្រឹមត្រូវ
              </Text>
            </div>
          )}

          {inputDelete === "DELETE" && (
            <div style={styles.successFeedback}>
              <MdOutlineCheckCircle style={{ color: "#52c41a", fontSize: 14 }} />
              <Text style={styles.successText}>
                បានបញ្ជាក់។ អ្នកអាចលុបបាន។
              </Text>
            </div>
          )}

          {/* Progress Indicator */}
          {inputDelete && inputDelete !== "DELETE" && (
            <Progress
              percent={Math.min((inputDelete.length / 6) * 100, 100)}
              showInfo={false}
              strokeColor="#ff4d4f"
              trailColor="#f0f0f0"
              size="small"
              style={styles.progress}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <Button
            size="large"
            onClick={onCancel}
            disabled={loading}
            style={styles.cancelButton}
          >
            បោះបង់
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            loading={loading}
            style={{
              ...styles.confirmButton,
              ...(isConfirmed && !loading ? styles.confirmButtonActive : {}),
            }}
          >
            {loading ? "កំពុងលុប..." : "លុប"}
          </Button>
        </div>

        {/* Footer Note */}
        <div style={styles.footerNote}>
          <Text type="secondary" style={styles.noteText}>
            <MdOutlineWarning style={{ fontSize: 12, marginRight: 4 }} />
            សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ
          </Text>
        </div>
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .delete-modal .ant-modal-content {
          border-radius: 20px;
          overflow: hidden;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-modal-body {
          padding: 24px;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-modal-close {
          top: 20px;
          right: 20px;
        }
        
        .delete-modal .ant-btn {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-alert {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-tag {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-input {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .delete-modal .ant-progress-text {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .ant-alert-message {
          color: #92400E !important;
          font-weight: 600 !important;
        }
        
        .ant-alert-description {
          color: #92400E !important;
        }
        
        .ant-alert-icon {
          color: #92400E !important;
        }
      `}</style>
    </Modal>
  );
};

const styles = {
  modal: {
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  modalTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  iconWrapper: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "#FEF2F2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteIcon: {
    fontSize: "18px",
    color: "#ef4444",
  },
  titleText: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1F2937",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  content: {
    padding: "8px 0",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  alert: {
    marginBottom: "20px",
    borderRadius: "12px",
    border: "1px solid #FEE2E2",
    backgroundColor: "#f63e3e35",
    color: "#92400E",
  },
  staffCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    marginBottom: "24px",
    border: "1px solid #E5E7EB",
  },
  staffAvatar: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarIcon: {
    fontSize: "28px",
    color: "#ef4444",
  },
  staffInfo: {
    flex: 1,
  },
  staffLabel: {
    fontSize: "11px",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffName: {
    margin: "4px 0 0 0",
    fontSize: "18px",
    fontWeight: 700,
    color: "#1F2937",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffPosition: {
    fontSize: "13px",
    color: "#6B7280",
    display: "block",
    marginTop: "2px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffId: {
    marginTop: "6px",
    fontSize: "11px",
    borderRadius: "6px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmationSection: {
    marginBottom: "24px",
  },
  confirmationHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  warningIcon: {
    fontSize: "18px",
    color: "#faad14",
  },
  confirmationText: {
    fontSize: "13px",
    color: "#92400E",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmInput: {
    height: "48px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  inputSuccess: {
    borderColor: "#52c41a",
    backgroundColor: "#F6FFED",
  },
  inputShake: {
    animation: "shake 0.5s ease-in-out",
  },
  errorFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#FEF2F2",
    borderRadius: "8px",
  },
  errorText: {
    fontSize: "12px",
    color: "#ff4d4f",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  successFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#F6FFED",
    borderRadius: "8px",
  },
  successText: {
    fontSize: "12px",
    color: "#52c41a",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  progress: {
    marginTop: "12px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
  },
  cancelButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    borderColor: "#E5E7EB",
    color: "#6B7280",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: "#E5E7EB",
    borderColor: "#E5E7EB",
    color: "#9CA3AF",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmButtonActive: {
    backgroundColor: "#ef4444",
    borderColor: "#ef4444",
    color: "white",
    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
  },
  footerNote: {
    textAlign: "center",
    paddingTop: "12px",
    borderTop: "1px solid #F3F4F6",
  },
  noteText: {
    fontSize: "11px",
    color: "#9CA3AF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
};

export default DeleteConfirmationModal;